<template>
  <article class="cr-row us-row" role="listitem">
    <div class="us-row__member">
      <span class="cr-row__field-label">Member</span>
      <div class="us-row__member-main">
        <div class="us-row__avatar" :class="avatarToneClass" aria-hidden="true">
          {{ initials }}
        </div>
        <div class="us-row__member-text">
          <span class="us-row__name" :title="user.name">{{ user.name || '—' }}</span>
          <span
            v-if="emailDisplay"
            class="us-row__email-sub"
            :title="emailDisplay"
          >
            {{ emailDisplay }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="!hideType" class="us-row__type">
      <span class="cr-row__field-label">Type</span>
      <span class="us-pill" :class="agentPillClass">
        <v-icon :icon="agentIcon" size="12" class="us-row__type-icon" />
        {{ agentLabel }}
      </span>
    </div>

    <div class="us-row__contact">
      <span class="cr-row__field-label">Contact</span>
      <span
        v-if="user.contactDisplay"
        class="us-row__contact-value text-truncate"
        :title="user.contactDisplay"
      >
        {{ user.contactDisplay }}
      </span>
      <span v-else class="us-row__empty">—</span>
    </div>

    <div class="us-row__role">
      <span class="cr-row__field-label">Role</span>
      <span
        v-if="user.role"
        class="us-pill"
        :class="rolePillClass(user.role)"
      >
        {{ formatLabel(user.role) }}
      </span>
      <span v-else class="us-row__empty">—</span>
    </div>

    <div class="us-row__status">
      <span class="cr-row__field-label">Status</span>
      <span
        v-if="user.status"
        class="us-pill"
        :class="statusPillClass(user.status)"
      >
        {{ statusLabel }}
      </span>
      <span v-else class="us-row__empty">—</span>
    </div>

    <div class="us-row__joined">
      <span class="cr-row__field-label">Joined</span>
      <div class="us-row__joined-text">
        <span class="cr-row__date">{{ dateLabel }}</span>
        <span v-if="timeLabel" class="cr-row__clock">{{ timeLabel }}</span>
      </div>
    </div>

    <div class="us-row__actions" @click.stop>
      <span class="cr-row__field-label">Actions</span>
      <UsersActionMenu
        :user="user"
        :current-user-id="currentUserId"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @toggle-status="$emit('toggle-status', $event)"
        @set-extension="$emit('set-extension', $event)"
      />
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import UsersActionMenu from '@/components/Users/UsersActionMenu.vue'
import { formatUserStatus } from '@/constants/userStatus'
import {
  formatIsoDate,
  formatLabel,
  formatTimeAmPm,
} from '@/utils/format'
import { agentTypeLabel, isAiAgent, resolveListAgentType } from '@/utils/orgUsers'

defineOptions({ name: 'UsersRow' })

const props = defineProps({
  user: { type: Object, required: true },
  hideType: { type: Boolean, default: false },
  currentUserId: { type: String, default: '' },
})

defineEmits(['edit', 'delete', 'reset-password', 'toggle-status', 'set-extension'])

const initials = computed(() => {
  const parts = String(props.user.name || '?').trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
  }
  return (parts[0] || '?').slice(0, 2).toUpperCase()
})

const dateLabel = computed(() => formatIsoDate(props.user.createdOn))
const timeLabel = computed(() => formatTimeAmPm(props.user.createdOn))

const statusLabel = computed(() => formatUserStatus(props.user.status))

const resolvedAgentType = computed(() => resolveListAgentType(props.user))
const isAi = computed(() => isAiAgent(resolvedAgentType.value))
const agentLabel = computed(() => agentTypeLabel(resolvedAgentType.value))
const agentIcon = computed(() => (isAi.value ? 'mdi-robot-outline' : 'mdi-account-outline'))
const emailDisplay = computed(() => {
  if (isAi.value) return ''
  const email = String(props.user.email ?? '').trim()
  return email && email !== '—' ? email : ''
})

const avatarToneClass = computed(() => {
  if (isAi.value) return 'us-row__avatar--ai'
  const role = String(props.user.role ?? '').toUpperCase()
  if (role === 'OWNER') return 'us-row__avatar--owner'
  if (role === 'ADMIN') return 'us-row__avatar--admin'
  return 'us-row__avatar--member'
})

function rolePillClass(role) {
  const key = String(role ?? '').toUpperCase()
  if (key === 'OWNER') return 'us-pill--owner'
  if (key === 'ADMIN') return 'us-pill--admin'
  if (key === 'MEMBER') return 'us-pill--member'
  return 'us-pill--neutral'
}

function statusPillClass(status) {
  const key = String(status ?? '').toUpperCase()
  if (key === 'ACTIVE') return 'us-pill--active'
  return 'us-pill--inactive'
}

const agentPillClass = computed(() =>
  isAi.value ? 'us-pill--ai' : 'us-pill--human',
)
</script>
